import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  RefreshCw,
  CreditCard,
  Wallet,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Search,
  Loader2,
} from "lucide-react";

const STATUS_BADGE = {
  active:               { color: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", icon: CheckCircle2, label: "Active" },
  trialing:             { color: "bg-blue-500/15 text-blue-700 dark:text-blue-400",          icon: CheckCircle2, label: "Trialing" },
  past_due:             { color: "bg-amber-500/15 text-amber-700 dark:text-amber-400",       icon: AlertTriangle, label: "Past due" },
  canceled:             { color: "bg-rose-500/15 text-rose-700 dark:text-rose-400",          icon: XCircle, label: "Canceled" },
  incomplete:           { color: "bg-zinc-500/15 text-zinc-700 dark:text-zinc-400",          icon: AlertTriangle, label: "Incomplete" },
  incomplete_expired:   { color: "bg-rose-500/15 text-rose-700 dark:text-rose-400",          icon: XCircle, label: "Expired" },
  APPROVAL_PENDING:     { color: "bg-zinc-500/15 text-zinc-700 dark:text-zinc-400",          icon: AlertTriangle, label: "Approval pending" },
  ACTIVE:               { color: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", icon: CheckCircle2, label: "Active" },
};

function StatusBadge({ status }) {
  const conf = STATUS_BADGE[status] || { color: "bg-zinc-500/15 text-zinc-700 dark:text-zinc-400", icon: AlertTriangle, label: status || "Unknown" };
  const Icon = conf.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${conf.color}`} data-testid={`sub-status-${status}`}>
      <Icon className="h-3 w-3" />
      {conf.label}
    </span>
  );
}

function ProviderBadge({ provider }) {
  if (provider === "stripe") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-indigo-500/15 text-indigo-700 dark:text-indigo-400">
        <CreditCard className="h-3 w-3" />
        Stripe
      </span>
    );
  }
  if (provider === "paypal") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-800 dark:text-amber-300">
        <Wallet className="h-3 w-3" />
        PayPal
      </span>
    );
  }
  return <span className="text-xs text-zinc-500">—</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (e) { return iso; }
}

export default function AdminSubscriptions() {
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [providerFilter, setProviderFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (providerFilter) params.provider = providerFilter;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/admin/subscriptions", { params });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [providerFilter, statusFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.items || [];
    return (data.items || []).filter((s) =>
      (s.user_email || "").toLowerCase().includes(q) ||
      (s.stripe_subscription_id || "").toLowerCase().includes(q) ||
      (s.paypal_subscription_id || "").toLowerCase().includes(q) ||
      (s.plan_id || "").toLowerCase().includes(q)
    );
  }, [data.items, search]);

  const refreshOne = async (sub) => {
    const id = sub.stripe_subscription_id;
    if (!id) {
      toast.info("Only Stripe subscriptions can be refreshed from Stripe API.");
      return;
    }
    setRefreshing(id);
    try {
      const { data: r } = await api.post(`/admin/subscriptions/${id}/refresh`);
      toast.success(`Refreshed · status=${r.subscription?.status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setRefreshing(null);
    }
  };

  const counts = data.counts || {};

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-subscriptions-page">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Admin</div>
          <h1 className="text-3xl font-bold tracking-tight">Subscriptions</h1>
          <p className="text-sm text-zinc-500 mt-1">Stripe + PayPal recurring subscriptions across all users</p>
        </div>

        {/* Counts strip */}
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
          {[
            ["Total", counts.total ?? 0, "bg-zinc-500/10 text-zinc-700"],
            ["Active", counts.active ?? 0, "bg-emerald-500/10 text-emerald-700"],
            ["Past due", counts.past_due ?? 0, "bg-amber-500/10 text-amber-700"],
            ["Canceled", counts.canceled ?? 0, "bg-rose-500/10 text-rose-700"],
            ["Stripe", counts.stripe ?? 0, "bg-indigo-500/10 text-indigo-700"],
            ["PayPal", counts.paypal ?? 0, "bg-amber-500/10 text-amber-800"],
          ].map(([label, value, cls]) => (
            <div key={label} className={`rounded-2xl border border-zinc-200 dark:border-zinc-800 p-3 ${cls}`} data-testid={`count-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="text-xs uppercase tracking-widest font-semibold opacity-70">{label}</div>
              <div className="text-2xl font-bold mt-0.5">{value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Search email, sub id, plan…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64 rounded-xl"
              data-testid="subs-search"
            />
          </div>
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 px-3 py-2 text-sm"
            data-testid="subs-provider-filter"
          >
            <option value="">All providers</option>
            <option value="stripe">Stripe</option>
            <option value="paypal">PayPal</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 px-3 py-2 text-sm"
            data-testid="subs-status-filter"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="trialing">Trialing</option>
            <option value="past_due">Past due</option>
            <option value="canceled">Canceled</option>
            <option value="incomplete">Incomplete</option>
          </select>
          <Button onClick={load} variant="outline" size="sm" className="rounded-xl" data-testid="subs-reload-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reload
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-sm text-zinc-500" data-testid="subs-loading">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-zinc-500" data-testid="subs-empty">No subscriptions match the filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="subs-table">
                <thead className="bg-zinc-50 dark:bg-zinc-900">
                  <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-4 py-3">Provider</th>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Period end</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Subscription ID</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => {
                    const subId = s.stripe_subscription_id || s.paypal_subscription_id || s.id;
                    return (
                      <tr key={subId} className="border-t border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50/50 dark:hover:bg-zinc-900/50" data-testid={`sub-row-${subId}`}>
                        <td className="px-4 py-3"><ProviderBadge provider={s.provider} /></td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{s.user_email || "—"}</div>
                          <div className="text-xs text-zinc-500">{s.user_id?.slice(0, 8) || ""}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold capitalize">{s.plan_id || "—"}</div>
                          <div className="text-xs text-zinc-500">{s.billing_period || ""}</div>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                        <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">
                          {fmtDate(s.current_period_end)}
                          {s.cancel_at_period_end && (
                            <div className="text-xs text-rose-500 font-semibold mt-0.5">Will cancel</div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-zinc-700 dark:text-zinc-300">
                          {s.amount ? `${Number(s.amount).toFixed(2)} ${(s.currency || "EUR").toUpperCase()}` : "—"}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-500">{subId}</td>
                        <td className="px-4 py-3 text-right">
                          {s.provider === "stripe" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="rounded-lg"
                              onClick={() => refreshOne(s)}
                              disabled={refreshing === s.stripe_subscription_id}
                              data-testid={`sub-refresh-${subId}`}
                            >
                              {refreshing === s.stripe_subscription_id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
